import React from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { nodePath, type DraftLine } from '../lib/contracts'
import type { CostTree } from '../lib/tree'
import MoneyInput from './MoneyInput'

interface Props { lines: DraftLine[]; tree: CostTree; disabled?: boolean; allowNegative?: boolean; showNote?: boolean; showProvisional?: boolean; onChange: (lines: DraftLine[]) => void }

/** One row per item: the item (with its path), the amount (free text → decimal string), a note (contracts only), remove.
 * With `showProvisional` (contract lines, part D spec §2.1) each row gets a "Rezerva" switch. */
const MoneyLinesEditor: React.FC<Props> = ({ lines, tree, disabled, allowNegative = false, showNote = true, showProvisional = false, onChange }) => {
  const { t } = useTranslation()
  const update = (index: number, patch: Partial<DraftLine>) => onChange(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)))
  if (!lines.length) {
    return <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">{t('CostControl_lines_editor_empty')}</p>
  }
  return (
    <div className="max-h-[50vh] overflow-y-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('CostControl_lines_item')}</TableHead>
            <TableHead className="w-44 text-right">{t('CostControl_lines_amount')}</TableHead>
            {showNote && <TableHead className="w-48">{t('CostControl_lines_note')}</TableHead>}
            {showProvisional && <TableHead className="w-24 text-center">{t('CostControl_provisional_switch')}</TableHead>}
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((l, i) => {
            const node = tree.byId.get(l.nodeId)?.activity
            const path = nodePath(tree, l.nodeId)
            return (
              <TableRow key={l.nodeId}>
                <TableCell>
                  {path && <div className="text-xs text-muted-foreground">{path}</div>}
                  <div className="font-medium">{node ? (node.code ? `${node.code} · ${node.name}` : node.name) : t('CostControl_lines_unknown_item')}</div>
                </TableCell>
                <TableCell>
                  <MoneyInput value={l.amount} onChange={(amount, valid) => update(i, { amount, valid })} disabled={disabled} allowNegative={allowNegative} className="h-8" aria-label={t('CostControl_lines_amount')} />
                </TableCell>
                {showNote && (
                  <TableCell>
                    <Input value={l.description} onChange={(e) => update(i, { description: e.target.value })} disabled={disabled} className="h-8" aria-label={t('CostControl_lines_note')} />
                  </TableCell>
                )}
                {showProvisional && (
                  <TableCell className="text-center">
                    <Switch checked={!!l.provisional} onCheckedChange={(provisional) => update(i, { provisional })} disabled={disabled} aria-label={t('CostControl_provisional_switch')} />
                  </TableCell>
                )}
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => onChange(lines.filter((_, k) => k !== i))}
                    disabled={disabled}
                    aria-label={t('CostControl_lines_remove')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
export default MoneyLinesEditor
